import React, { useCallback, useEffect, useRef, useState } from 'react';
import Stepper, { STEPS, STEP_TAX39A } from './Stepper';
import Step1Billing from './Step1Billing';
import Tax39A from './Tax39A';
import EmailGate from './EmailGate';
import OrderSummary from './OrderSummary';
import PaymentMethods from './PaymentMethods';
import Step3Review from './Step3Review';
import CodPhoneVerify, { needsPhoneVerify, normMobile } from './CodPhoneVerify';
import { EMPTY_ADDR } from './fields';
import { classifyGroup, isPickup } from './ShippingMethods';
import { should39aStep, normalizeAfm } from './tax39a-gate';
import { sameReducedIsland, isReducedVatPostcode } from './island-vat';
import { recalcShipping, setCartQty, setPaymentMethod, tax39aCheck, islandVatState } from './auth';

const cfg = (typeof window !== 'undefined' && window.CHECKOUT_NEXT) || {};

// Shipping group requested by the cart (?ship=pickup|courier|boxnow) → resolve to
// a concrete method id from the available list so checkout opens preselected.
function preselectedMethod() {
  const chosen = (cfg.shipping && cfg.shipping.chosen) || '';
  let group = '';
  try { group = new URLSearchParams(window.location.search).get('ship') || ''; } catch (e) { /* no URL */ }
  if (!group) return chosen;
  const methods = (cfg.shipping && cfg.shipping.methods) || [];
  // Prefer the cheapest method of the requested group (cart picks the cheapest).
  const inGroup = methods.filter((m) => classifyGroup(m.id) === group);
  if (!inGroup.length) return chosen;
  if (inGroup.some((m) => m.id === chosen)) return chosen; // session already on it
  return inGroup[0].id;
}

function initialStep1(identity) {
  const addrs = cfg.addresses || {};
  const billingBook = addrs.billing || [];
  const shippingBook = addrs.shipping || [];
  const inv = addrs.invoice || {};
  const fb = billingBook[0];
  const fs = shippingBook[0];
  const email = cfg.userEmail || (identity && identity.channel === 'email' ? identity.identifier : '') || (fb && fb.fields.email) || '';
  const phone = cfg.userPhone || (identity && identity.channel === 'phone' ? identity.identifier : '') || (fb && fb.fields.phone) || '';

  const docType = addrs.lastDocType || 'receipt';
  // Prefill the invoice profile whenever the customer HAS saved invoice details —
  // independent of the default doc-type — so switching to «Τιμολόγιο» reuses their
  // latest ΑΦΜ/company/ΔΟΥ (mirrors how the απόδειξη address is prefilled).
  const hasInvoice = !!(inv.billing_company || inv.billing_vat);

  return {
    docType,
    afm: inv.billing_vat || '',
    lookupStatus: hasInvoice ? 'valid' : 'idle',
    invoice: hasInvoice ? { ...inv } : {},
    billing: fb ? { ...EMPTY_ADDR, ...fb.fields, email: fb.fields.email || email } : { ...EMPTY_ADDR, email },
    billingAddrId: fb ? fb.id : 'new',
    shipToBilling: true,
    shipping: fs ? { ...EMPTY_ADDR, ...fs.fields } : { ...EMPTY_ADDR },
    shippingAddrId: fs ? fs.id : 'new',
    shippingMethod: preselectedMethod(),
    contactPhone: phone,
    deliveryNote: '',
  };
}

// Always start at billing. (Step-resume-across-refresh via sessionStorage was
// removed — it caused edge-case bugs when the cart/gates changed on reload.)
function initialStep() {
  return 'billing';
}

export default function CheckoutNext() {
  const startIdentified = cfg.isLoggedIn && !cfg.forceGuest;
  const [identity, setIdentity] = useState(
    startIdentified ? { mode: 'account', identifier: cfg.userEmail || '', channel: 'email' } : null
  );

  const [current, setCurrent] = useState(initialStep);
  const [step1, setStep1] = useState(() => initialStep1(startIdentified ? { channel: 'email', identifier: cfg.userEmail } : null));
  // Conditional «ΦΠΑ 39Α» step — appears only when the cart has eligible products,
  // τιμολόγιο is chosen, AND the invoice ΑΦΜ is CONFIRMED 39Α-eligible (AADE Su1).
  const t39Init = cfg.tax39a || {};
  const [afm39aStatus, setAfm39aStatus] = useState((t39Init.afmStatus && t39Init.afmStatus.status) || 'none');
  const checked39aAfm = useRef(t39Init.afm || '');
  const invoiceAfm = normalizeAfm(step1.invoice && step1.invoice.billing_vat);
  // When an invoice ΑΦΜ is entered/selected (and products are eligible), validate it
  // once with AADE — a 'validated' result reveals the 39Α step.
  useEffect(() => {
    if (!(t39Init.available) || step1.docType !== 'invoice') return;
    if (invoiceAfm.length !== 9 || invoiceAfm === checked39aAfm.current) return;
    checked39aAfm.current = invoiceAfm;
    tax39aCheck(invoiceAfm).then(({ ok, data }) => setAfm39aStatus(ok ? (data.status || 'none') : 'none'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step1.docType, invoiceAfm]);
  const has39a = should39aStep(cfg.tax39a, step1.docType, afm39aStatus);
  const steps = has39a ? [STEPS[0], STEP_TAX39A, STEPS[1]] : STEPS;
  // If the 39Α step is open but the gate no longer holds, don't strand the user on it.
  useEffect(() => { if (current === 'tax39a' && !has39a) setCurrent('payment'); }, [has39a, current]);

  // Funnel: per-step view for the NEW checkout (mirrors the old CheckoutWC step tracking,
  // so we can compare intra-checkout drop-off between the two flows). Fires once per step
  // change; PostHog auto-attaches the experiment variant. Never throws.
  useEffect(() => {
    try {
      if (typeof window === 'undefined' || !window.posthog || typeof window.posthog.capture !== 'function') return;
      const map = {
        billing: { step_label: 'contact_billing', step_index: 1 },
        tax39a:  { step_label: 'tax39a',          step_index: 2 },
        payment: { step_label: 'payment',         step_index: 3 },
      };
      const m = map[current];
      if (m) window.posthog.capture('checkout_step_viewed', { checkout_flow: 'new', step: current, step_label: m.step_label, step_index: m.step_index });
    } catch (e) { /* noop */ }
  }, [current]);

  // Reduced island VAT (17%) — OFF unless enabled server-side. Establishment = the
  // AADE-registered (billing) ΤΚ; goods shipped to an island too. 39Α wins server-side.
  const islandEnabled = !!(cfg.islandVat && cfg.islandVat.enabled);
  // Init from the server's current flag so a STALE reduction (e.g. left over from a
  // prior session / test) gets reconciled + cleared when this order isn't eligible.
  const [islandApplied, setIslandApplied] = useState(!!(cfg.islandVat && cfg.islandVat.applied));
  const estZip = (step1.billing && step1.billing.postcode) || '';
  const shipZip = ((step1.shipToBilling ? step1.billing : step1.shipping) || {}).postcode || '';
  useEffect(() => {
    if (!islandEnabled) return;
    // FALLBACK: only reduce when the establishment ΤΚ is AADE-VERIFIED (the ΑΦΜ
    // lookup succeeded). If AADE is down / not looked-up, the billing ΤΚ is just
    // user-typed and must NOT trigger the reduction → default 24%.
    const aadeVerified = step1.lookupStatus === 'valid';
    const invAfm = normalizeAfm(step1.invoice && step1.invoice.billing_vat);
    const testAfm = (cfg.islandVat && cfg.islandVat.testAfm) || '';
    const isTestAfm = !!testAfm && invAfm === testAfm; // test VAT key — simulates an island seat
    // Goods must ship to the SAME island the business is established on. The test ΑΦΜ
    // simulates an island seat AND bypasses the AADE-verified gate (a dummy number can't
    // be validated at ΑΑΔΕ), but STILL requires shipping to a qualifying island ΤΚ —
    // otherwise a mainland delivery (e.g. Athens) or a non-reduced island (e.g. Corfu)
    // would wrongly trigger 17%. The real path keeps the AADE-verified requirement.
    const eligible = step1.docType === 'invoice' && (
      isTestAfm
        ? isReducedVatPostcode(shipZip)
        : (aadeVerified && sameReducedIsland(estZip, shipZip))
    );
    if (!eligible && !islandApplied) return;
    islandVatState({ estZip, shipZip, afm: invAfm, apply: eligible ? 1 : 0, verified: aadeVerified ? 1 : 0 }).then(({ ok, data }) => {
      if (!ok) return;
      setIslandApplied(!!(data.island && data.island.applied));
      if (data.cart) setCart(data.cart);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step1.docType, estZip, shipZip, step1.lookupStatus, step1.invoice && step1.invoice.billing_vat]);

  // Cart + shipping are live: recalculated whenever the destination changes.
  const [cart, setCart] = useState(cfg.cart || null);
  const [shipping, setShipping] = useState(cfg.shipping || { methods: [], chosen: '' });
  const [shippingLoading, setShippingLoading] = useState(false);
  const [payBusy, setPayBusy] = useState(false);
  const [payment, setPayment] = useState((cfg.payments && cfg.payments.chosen) || '');
  // Live payment list — availability depends on the chosen shipping method
  // (e.g. COD is hidden for store pickup, COP shown), so it refreshes with it.
  const [payments, setPayments] = useState(cfg.payments || { methods: [], chosen: '' });
  const [cartBusy, setCartBusy] = useState(false);

  // Αντικαταβολή (COD) — the contact mobile must have passed an SMS code once.
  // We track WHICH number was verified (not a bare boolean) so editing the phone
  // back in step 1 re-closes the gate instead of riding on a stale "verified".
  const [verifiedPhone, setVerifiedPhone] = useState(
    (cfg.phoneVerify && cfg.phoneVerify.verified) ? normMobile(cfg.userPhone || '') : ''
  );
  const contactNorm = normMobile(step1.contactPhone);
  const phoneVerified = !!contactNorm && contactNorm === verifiedPhone;

  // ---- COD funnel diagnostics ---------------------------------------------
  // Fired on SELECTING Αντικαταβολή, not on reaching the payment step: COD sorts
  // second-to-last and is never pre-chosen, so that click is the first moment the SMS
  // gate can cost anything — the tightest honest denominator for "did the gate lose us
  // this order". Built as the exposure metric of the A/B that ended 2026-08-18 (the gate
  // shipped to everyone) and kept afterwards, because `cod_abandoned_for` is now the
  // only standing measure of what the gate still costs.
  //
  // It fires for shoppers whose mobile is ALREADY verified as well — tagged
  // (`phone_already_verified`), never dropped: they are exactly the returning customers
  // the gate is free for, and losing them from the denominator would flatter it.
  const prevPaymentRef = useRef(null); // null so a checkout that OPENS on COD still counts
  const codExposedRef = useRef(false);
  useEffect(() => {
    if (prevPaymentRef.current === payment) return;
    const prev = prevPaymentRef.current;
    prevPaymentRef.current = payment;
    try {
      if (typeof window === 'undefined' || !window.posthog || typeof window.posthog.capture !== 'function') return;
      const base = { checkout_flow: 'new' };
      if (payment === 'cod' && !codExposedRef.current) {
        codExposedRef.current = true;
        const noPayment = !!(cart && cart.needsPayment === false);
        window.posthog.capture('cod_selected', {
          ...base,
          // Does THIS shopper actually meet the gate? Already-verified numbers and
          // fully wallet-covered orders sail through even in the test arm.
          gate_shown: !noPayment && needsPhoneVerify('cod') && !phoneVerified,
          phone_already_verified: phoneVerified,
          fully_wallet_covered: noPayment,
          // totalMinor, not `total` — the latter is a formatted "€123,45" string and
          // PostHog can't average that.
          cart_total: cart && typeof cart.totalMinor === 'number' ? cart.totalMinor / 100 : null,
        });
      } else if (prev === 'cod' && payment !== 'cod' && codExposedRef.current) {
        // The headline diagnostic: someone who wanted COD walked away from it.
        window.posthog.capture('cod_abandoned_for', {
          ...base,
          switched_to: payment,
          phone_verified_first: phoneVerified,
        });
      }
    } catch (e) { /* analytics must never block checkout */ }
  }, [payment]); // eslint-disable-line react-hooks/exhaustive-deps

  // Selecting a payment method applies its fee (COD surcharge, PayPal fee…) and
  // refreshes the totals in the summary.
  const changePayment = useCallback(async (id) => {
    setPayment(id);
    setPayBusy(true);
    const { ok, data } = await setPaymentMethod(id);
    setPayBusy(false);
    if (!ok) return;
    if (data.cart) setCart(data.cart);
    if (data.payments) setPayments(data.payments);
  }, []);

  const changeQty = useCallback(async (key, qty) => {
    setCartBusy(true);
    const { ok, data } = await setCartQty(key, qty);
    setCartBusy(false);
    if (!ok) return;
    if (data.cart) setCart(data.cart);
    if (data.shipping) setShipping(data.shipping);
    if (data.payments) setPayment((prev) => (data.payments.chosen && !prev ? data.payments.chosen : prev));
  }, []);

  // Latest-wins: two quick shipping clicks fire two recalcs, and without this the
  // slower (older) response could land last and repaint the lists — showing, say,
  // the counter's "pay by card on collection" next to the 24/7 locker.
  const shipSeq = useRef(0);
  const refreshShipping = useCallback(async (dest, method) => {
    const seq = ++shipSeq.current;
    setShippingLoading(true);
    const { ok, data } = await recalcShipping({
      postcode: dest.postcode || '',
      city: dest.city || '',
      state: dest.state || '',
      method: method || '',
    });
    if (seq !== shipSeq.current) return; // superseded by a newer request
    setShippingLoading(false);
    if (!ok) return;
    if (data.cart) setCart(data.cart);
    if (data.shipping) {
      setShipping(data.shipping);
      // Keep the requested method ONLY if it's still selectable in the fresh list;
      // otherwise fall back to the server's resolved default. Without this, a method
      // from a previous zone lingers as the selection after an address change — no
      // radio renders for it, yet it's truthy so the user can wrongly "proceed".
      const enabledIds = (data.shipping.methods || []).filter((m) => !m.disabled).map((m) => m.id);
      const next = (method && enabledIds.indexOf(method) !== -1)
        ? method
        : (data.shipping.chosen || enabledIds[0] || '');
      setStep1((s) => ({ ...s, shippingMethod: next }));
    }
    // Shipping change can change which payment methods are available.
    if (data.payments) {
      setPayments(data.payments);
      setPayment((prev) => {
        const list = (data.payments.methods || []).map((m) => m.id);
        if (prev && list.indexOf(prev) !== -1) return prev; // still valid
        return data.payments.chosen || list[0] || '';       // else fall back
      });
    }
  }, []);

  function onIdentified(id) {
    setIdentity(id);
    if (id && id.identifier) {
      if (id.channel === 'email') setStep1((s) => ({ ...s, billing: { ...s.billing, email: s.billing.email || id.identifier } }));
      if (id.channel === 'phone') setStep1((s) => ({ ...s, contactPhone: s.contactPhone || id.identifier }));
    }
  }

  // Arriving via ?ship=pickup: sync store pickup once on mount so the server
  // calculates against the shop (dest-independent), locking in :37 + free
  // shipping and unlocking COP — otherwise the pickup rate stays unselected in
  // the session and COP (gated to :37) would show as unavailable.
  useEffect(() => {
    let group = '';
    try { group = new URLSearchParams(window.location.search).get('ship') || ''; } catch (e) { /* no URL */ }
    if (group !== 'pickup') return;
    const pm = ((shipping && shipping.methods) || []).find((m) => isPickup(m.id));
    if (!pm || (shipping && shipping.chosen === pm.id)) return; // nothing to do / already synced
    const d = step1.shipToBilling ? step1.billing : (step1.shipping || {});
    refreshShipping({ postcode: d.postcode, city: d.city, state: d.state }, pm.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply the pre-selected method's fee as soon as the payment step opens.
  useEffect(() => {
    if (current === 'payment' && payment) changePayment(payment);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);

  // Scroll back to the top on every step change (the Next/Back buttons sit at the
  // bottom, so on mobile you'd otherwise land mid-way down the new step).
  useEffect(() => {
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [current]);

  if (!identity) {
    return <EmailGate onIdentified={onIdentified} />;
  }

  return (
    <div className="cn-layout">
      <main className="cn-main">
        <a className="cn-backlink" href={cfg.cartUrl || '/cart/'}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 18l-6-6 6-6" /></svg>
          Επιστροφή στο καλάθι
        </a>
        <Stepper current={current} onNavigate={setCurrent} steps={steps} />
        {islandApplied && (
          <div className="cn-island-note">
            Εφαρμόστηκε μειωμένος ΦΠΑ 17% (έδρα επιχείρησης σε νησί). Εκκρεμεί επιβεβαίωση από το κατάστημα.
          </div>
        )}
        <div className="cn-body">
          {current === 'billing' && (
            <Step1Billing
              value={step1}
              onChange={setStep1}
              shipping={shipping}
              shippingLoading={shippingLoading}
              onRecalc={refreshShipping}
              onNext={() => setCurrent(has39a ? 'tax39a' : 'payment')}
            />
          )}
          {current === 'tax39a' && has39a && (
            <Tax39A
              value={step1}
              cart={cart}
              onCart={setCart}
              onNext={() => setCurrent('payment')}
              onBack={() => setCurrent('billing')}
              onEditBilling={() => setCurrent('billing')}
            />
          )}
          {current === 'payment' && (
            <section className="cn-step1">
              <button type="button" className="cn-steplink" onClick={() => setCurrent(has39a ? 'tax39a' : 'billing')}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 18l-6-6 6-6" /></svg>
                Πίσω
              </button>
              {cart && cart.needsPayment === false ? (
                <>
                  <h3 className="cn-section-title">Πληρωμή</h3>
                  <p className="cn-review__muted cn-fullycovered">
                    Η παραγγελία καλύπτεται εξ ολοκλήρου από το υπόλοιπο του πορτοφολιού σου — δεν υπάρχει ποσό προς πληρωμή.
                  </p>
                </>
              ) : (
                <>
                  <h3 className="cn-section-title">Τρόπος πληρωμής</h3>
                  <PaymentMethods
                    data={payments}
                    value={payment}
                    onChange={changePayment}
                    renderUnder={(m) => (needsPhoneVerify(m.id) ? (
                      <CodPhoneVerify
                        phone={step1.contactPhone}
                        verified={phoneVerified}
                        onVerified={setVerifiedPhone}
                        method={m.id}
                      />
                    ) : null)}
                  />
                </>
              )}
              {/* Review step removed — gift-wrap add-on + terms + place-order live
                  at the bottom of this (final) payment step. */}
              <Step3Review
                value={step1}
                payment={payment}
                payments={payments}
                cart={cart}
                onCart={setCart}
                phoneVerified={phoneVerified}
                loading={shippingLoading || payBusy || cartBusy}
              />
            </section>
          )}
        </div>
      </main>

      <OrderSummary variant="sidebar" cart={cart} onQty={changeQty} busy={cartBusy} docType={step1.docType} />
      <OrderSummary variant="mobile" cart={cart} onQty={changeQty} busy={cartBusy} docType={step1.docType} />
    </div>
  );
}
